import { Skill } from "../types/prisma-enums";
import { K3D_ITEMS } from "./kalqy-world.items";
import type { Localized } from "../i18n";
import type { GameContent, Level } from "./types";

/* ── the teach-through, spoken — §8 ─────────────────────────────────────────── */

/**
 * 🔴 THE GATE ALREADY TEACHES; IT HAD NOTHING TO SAY. `gateCycle` runs §8's
 * three beats after a wrong answer — name what was chosen, show the right one,
 * move on — and lights the right card, but no line existed for any beat, so a
 * child who missed heard silence for four seconds and then the run resumed.
 * These are those lines, and a right answer's praise.
 *
 * ⚠️ NEUTRAL BY CONSTRUCTION. *"You chose the cow"* is an acknowledgement, not a
 * verdict — §8: no buzzer, no red X, no negative sound — and the order (named,
 * shown, onward) is `gateCycle`'s, never reshuffled here.
 *
 * 🔴 KEYED BY CARD, AND THE SCENE AND `generate-voice-lines.mjs` BOTH CALL
 * THESE FUNCTIONS. One key rule in two places is a voice saying one card while
 * the screen lights another, with nothing to notice it.
 */
export const k3dChoseKey = (card: string | number): string => `k3d.chose.${String(card)}`;
export const k3dHereKey = (card: string | number): string => `k3d.here.${String(card)}`;
/**
 * Rotated, for the reason `RUN_WITH_ME` gives: one line eight times a run is noise.
 *
 * ⚠️ NO "ONWARD" LINE. The third beat is already spoken — the scene says *"run
 * with me"* the moment the gate opens — and a second line there would stack two
 * sentences on one moment.
 */
export const K3D_FOUND_KEYS = ["k3d.found.1", "k3d.found.2", "k3d.found.3"] as const;

/** A card id as a child hears it — `red-balloon` is "red balloon". */
const thingOf = (card: string): string => card.replace(/-/g, " ");

export const K3D_FEEDBACK_LINES: Readonly<Record<string, Localized<string>>> = (() => {
  const lines: Record<string, Localized<string>> = {
    "k3d.found.1": { en: "Yes! You found it!" },
    "k3d.found.2": { en: "That's the one! Well done!" },
    "k3d.found.3": { en: "You found it! Super!" },
  };
  for (const card of new Set(K3D_ITEMS.flatMap((it) => it.choices.map(String)))) {
    lines[k3dChoseKey(card)] = { en: `You chose the ${thingOf(card)}.` };
    lines[k3dHereKey(card)] = { en: `Here is the ${thingOf(card)}.` };
  }
  return lines;
})();

/**
 * Kalqy World — Social Awareness.
 *
 * Authored against `HELPING_HANDS_L1_SCRIPT_v3.md` and
 * `PROGRESSION_MODEL_3_LEVELS.md`. Three levels, mastery-gated.
 *
 * ─── §1 · the mechanic, identical at all three levels ───────────────────────
 *
 *     SCENE (8–12s) → CHOOSE (point + 600ms dwell) → ACT (gesture) → PAYOFF (6–8s)
 *
 * 🔴 ONLY THE CONTENT OF THE CHOICE CHANGES ACROSS L1–L3. The gesture pool and
 * every threshold stay fixed, so "a level-3 wave is the same wave as level 1".
 * What changes is choice count (2 → 3 → 4), distractor subtlety, and step count.
 *
 * 🔴 THE GESTURE POOL IS UPPER-BODY ONLY, AND IT IS CLOSED. Kalqy World is the
 * game a seated child completes "without a single concession". A scene may not
 * ask for anything outside `HelpActName`, and that union has no lower-body
 * member — so a level author cannot write a scene a wheelchair user cannot
 * finish, which is a stronger guarantee than a review comment.
 *
 * ─── §2 · what level and band each do ───────────────────────────────────────
 *
 * **Level** is what advances, what gates and what a parent reads ("Level 2 of
 * 3"). **Band** selects which scenes inside a level a child sees. A 3-year-old
 * and a 5-year-old can both sit at L1 and meet different ponds.
 *
 * ⚠️ CONTENT VOLUME IS SHORT OF SPEC. PROGRESSION §6 budgets **18 scenes per
 * level** (6 per band). These tables carry 12 · 13 · 13 — above the "~12 or it
 * feels thin by week two" floor and far above the ≥2× itemCount pool rule, but
 * the remaining ~5 per level are a real authoring debt, not an oversight.
 *
 * ⚠️ AND THE BANDS ARE UNEVEN: 4·4·4 at L1, but 4·4·5 at L2 and L3. HH-4 added
 * the fourth BAND_3_4 scene to each of L2 and L3 — the youngest band had three
 * against five, so the child with the shortest attention span was the one
 * meeting the same scene most often. **Nothing selects on band at runtime yet**
 * (see `docs/engineering/level-authoring.md` §"what band does not do"), so this
 * is authoring the tables correctly ahead of the selection that reads them.
 *
 * ─── §3 · ADR-014, and L3 is where it bites ─────────────────────────────────
 *
 * L3's feelings scenes are answered by **pointing to** a face, never by making
 * one. Nothing about a child's own expression is scored, stored or reported, and
 * `check-game-contract.mjs` bans a game folder so much as referencing a facial
 * signal.
 *
 * ⚠️ THE MALAYALAM COLUMN OF THIS TABLE IS GONE (ADR-044).
 */

/**
 * The picture vocabulary — actions, people and objects a child recognises.
 *
 * A distractor must be **plausible**, or the child eliminates by absurdity and
 * the item stops being evidence of anything. Here that matters more than usual,
 * because every distractor is *also a possible behaviour* — the wrong answers
 * are things a child might really do, not nonsense.
 */
export const SCENES = [
  // greetings and manners — L1
  "wave-hello",
  "wave-goodbye",
  "namaste",
  "turn-away",
  "cover-ears",
  "look-down",
  // sharing and turns — L2
  "share-toy",
  "give-half",
  "wait-turn",
  "grab-toy",
  "hide-toy",
  // helping — L2
  "pick-up-toys",
  "carry-bag",
  "water-plant",
  "walk-past",
  "watch-only",
  // feelings in others — L3. Pointed at, never performed (ADR-014).
  "face-sad",
  "face-happy",
  "face-scared",
  "face-angry",
  "face-tired",
  // safety and simple rules — L3
  "hold-hand",
  "look-both-ways",
  "run-across",
  "ask-adult",
  "touch-hot",
  "stay-close",
  "walk-with-stranger",
  "tell-teacher",
  /* The pond cast — the set is shared with Sky Quest and Bridge Patterns. */
  "pip-bird",
  "grandpa-frog",
  "lily-pad",
  "the-ball",
] as const;

export type Scene = (typeof SCENES)[number];

/**
 * 🔴 THE SCENE BUILDERS WERE REMOVED WITH THE SCENES THEY BUILT.
 *
 * `beat`, `times` and `scene` existed only to author the Helping Hands item
 * pools; with `items: []` at all three levels they had no call sites left, and
 * a helper kept "for when the content comes back" is a helper the next author
 * fills in from guesswork rather than from the new game's script. Re-derive them
 * from whatever Kalqy World's own scene shape turns out to be.
 *
 * ⚠️ `SCENES` / `Scene` ABOVE DELIBERATELY SURVIVED. That table is the ART
 * vocabulary — `shared/ui/gameArt.ts` and `shared/art/art.test.ts` key off it,
 * and the pond cast at its foot is shared with Sky Quest and Bridge Patterns.
 * It is not level content and deleting it would take two other games with it.
 */
/* ══════════════════════════════════════════════════════════════════════════ */
/* L1 · COLOURS, SHAPES & ANIMAL SOUNDS — 3 lanes, band 3–4                    */
/* ══════════════════════════════════════════════════════════════════════════ */

/**
 * L1_SCRIPT §2 — "Come to the pond". Pip the bird arrives, Grandpa Frog returns
 * the ball, evening comes and everyone waves goodbye.
 *
 * 🔴 TWO CARDS, AND THE DISTRACTOR IS NEVER ABSURD. A child guessing gets ~50%,
 * which is exactly why mastery needs the 3-round exposure floor rather than a
 * single good round (PROGRESSION §2).
 *
 * 🔴 GOODBYE REUSES THE WAVE FROM SCENE A ON PURPOSE. "A three-year-old gets to
 * succeed at something they already learned once this round, and it costs no new
 * detection work."
 */
const L1: Level = {
  level: 1,
  /*
   * ⚠️ NAMED FOR WHAT IS ASKED. Every gate at every level asks *"where is the
   * hat / cow / triangle?"* — real things, as instructed (`build_k3d_questions.py`)
   * — and the level is the WORLD the trail runs through. The old names
   * ("Come to the pond", "Helping hands", "Feelings and safety") and their
   * objectives described greeting and feelings scenes no child is ever shown.
   */
  name: { en: "Find it: Sunny Forest" },
  objective: {
    en: "Hear the name of a real thing, then point to it",
  },
  ncf: ["C-4.1", "C-5.1", "C-12.2"],
  /**
   * Six gates, fixed, not scaled by age — the Short Trail (O5), so every level
   * of every game is six questions. One question per gate (`GATE_COUNT`).
   */
  itemCount: 6,
  /**
   * 🔴 POINT-AND-HOLD, THREE LANES — the answer verb, and not `step`.
   * D3 freezes lane-stepping entirely while a gate is open, so modelling the
   * answer as a step would collapse the one distinction that keeps a wrong
   * answer from being an accident. See `MotorAction` in types.ts.
   */
  motorAction: { kind: "point", zones: 3 },
  /**
   * 🔴 GENERATED — scripts/k3d/build_k3d_questions.py. Never hand-edited.
   *
   * ⚠️ ALL THREE LEVELS SHARE ONE POOL, AND THAT IS D1 IN THE DATA. Level is the
   * WORLD (art, speed, obstacle density); BAND selects which items inside it a
   * child meets. A six-year-old starting fresh plays World 1 with rhymes and
   * addition; a three-year-old who reaches World 3 still gets colours. Splitting
   * the pool per level would bind curriculum to motor progression — the exact
   * thing D1 exists to prevent.
   */
  items: K3D_ITEMS,
};

/* ══════════════════════════════════════════════════════════════════════════ */
/* L2 · awaiting band 4–5 authoring — 3 lanes                                  */
/* ══════════════════════════════════════════════════════════════════════════ */

/**
 * L1_SCRIPT §7 — "A friend has no lunch · toys scattered · a lily droops."
 * Gestures: `reach` (share), `lift` (carry), `lean` (tilt to pour).
 *
 * ⚠️ `lean` IS PORTED FIELD DATA (0.055 / 0.030) and `arms-up` is too. Mapping
 * content onto gestures already measured means two of three levels need no new
 * tuning at all — `wave`, `hands-together`, `reach` and `lift` are the four to
 * tune at playtest.
 */
const L2: Level = {
  level: 2,
  name: { en: "Find it: Moonlit Meadow" },
  objective: {
    en: "Find things, animals and colours on a faster trail",
  },
  ncf: ["C-4.1", "C-4.2", "C-5.1"],
  /*
   * 🔴 SIX, BECAUSE THE TRAIL HAS SIX GATES IN EVERY WORLD (`GATE_COUNT`).
   * It was once 3 against more gates, and the extra gates then stopped the
   * runner with no question, no voice and no cards of their own, and graded
   * whatever the child pointed at against an answer nobody was asked. Same as L1.
   */
  itemCount: 6,
  motorAction: { kind: "point", zones: 3 },
  items: K3D_ITEMS,
};

/* ══════════════════════════════════════════════════════════════════════════ */
/* L3 · awaiting band 5–6 authoring — 3 lanes                                  */
/* ══════════════════════════════════════════════════════════════════════════ */

/**
 * L1_SCRIPT §7 — "Who on the pads looks sad? · What is safe to touch?"
 *
 * 🔴 ADR-014, AND THIS IS THE LEVEL IT EXISTS FOR. Every feelings scene asks the
 * child to **point to** a feeling on someone else's face — never to perform one.
 * "A camera detects a coarse blendshape, not an emotion." The acted gesture is a
 * namaste (comfort offered) or arms-up (stop) — both social acts, neither a
 * facial expression and neither a report on how the child feels.
 */
const L3: Level = {
  level: 3,
  name: { en: "Find it: Rainbow Valley" },
  objective: {
    en: "Find things, animals, colours and shapes at full speed",
  },
  ncf: ["C-4.2", "C-5.1", "C-12.2"],
  /* 🔴 SIX — one question per gate. See L2. */
  itemCount: 6,
  motorAction: { kind: "point", zones: 3 },
  items: K3D_ITEMS,
};

export const KALQY_WORLD: GameContent = {
  id: "kalqy-world",
  category: "SOCIAL_AWARENESS",
  contentSkill: Skill.SOCIAL_AWARENESS,
  /**
   * Acting out a wave or a two-handed lift is coordination, and knowing where
   * your own hands are in relation to another person is body awareness.
   */
  motorSkills: [Skill.BODY_AWARENESS, Skill.COORDINATION],
  emoji: "🤝",
  /**
   * 🔴 "high" — THE CHILD RUNS, STEPS, JUMPS AND SQUATS FOR FOUR TO SIX MINUTES.
   *
   * It said "medium" because this slot used to hold Helping Hands, which asked
   * for upper-body gestures from a seated child. `EXERTION_LABELS` renders this
   * as "Lots of moving", which is what a teacher choosing a game before nap
   * time actually needs to know.
   *
   * ⚠️ IT WAS NOT A ONE-WORD CHANGE. Three high-exertion games broke a greedy
   * session picker that could only interleave two — sessions dropped from five
   * rounds to four and Letter Lagoon L1 stopped being offered from a fresh
   * profile. Correcting a label caused a curriculum regression in a different
   * game, which is why `builder.ts` grew one step of lookahead.
   *
   * ⚠️ STILL "medium", AND THE REASON IS ARITHMETIC RATHER THAN A BUG.
   *
   * `builder.ts` now has the lookahead that stops a session ending a round
   * short, and it works — but three high-exertion games and two low ones cannot
   * be interleaved past FIVE rounds under "never two highs in a row". Measured:
   *
   *     exertion medium :  sessions up to 6 rounds, LL worlds [1,2,3]
   *     exertion high   :  sessions exactly 5,      LL worlds [2,3]
   *
   * Letter Lagoon opens at its learning EDGE (L3), so L1 is only reached on a
   * third visit — and a five-round session across five games has no room for a
   * third visit to anything. So correcting this label costs a round AND costs
   * Letter Lagoon's D1 guarantee that all three worlds are schedulable.
   *
   * ✅ DECIDED: keep "medium". Six-round sessions and Letter Lagoon's D1
   * guarantee are worth more than a truthful exertion label, which is read by
   * an adult choosing a game and by nothing else. The trade is recorded above
   * so the next reader knows the field is wrong ON PURPOSE and what it would
   * cost to fix — the failure mode this comment exists to prevent is somebody
   * "correcting" it in a tidy-up and taking a round off every child's session.
   */
  exertion: "medium",
  /**
   * 🔴 WHAT THE BODY DOES BETWEEN GATES. `motorAction` says how a child ANSWERS
   * — pointing — and that is deliberately all it says. This is the obstacle
   * course, and without it the game's gross-motor core is invisible to
   * `crossGame.test.ts` and to every adult screen that reads the curriculum.
   */
  movement: ["step", "jump", "duck"],
  levels: [L1, L2, L3],
};
